import { useEffect, useMemo, useState } from 'react';
import type {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { LocateFixed, MapPin, Plus, Trash2 } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { FormFieldWide, FormSection } from '@/components/ui/FormSection';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Select } from '@/components/ui/Select';
import { districtOptionsFor } from '@/constants/districts';
import {
  BOARD_OPTIONS,
  INSTITUTION_TYPE_OPTIONS,
  CLASS_LEVEL_OPTIONS,
  COLLEGE_LEVEL_OPTIONS,
  GENDER_TYPE_OPTIONS,
  MANAGEMENT_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  RESIDENTIAL_OPTIONS,
  SCHOOL_LEVEL_OPTIONS,
  INDIAN_STATES,
  STATE_OPTIONS,
} from '@/constants/institute';
import { useCurrentAddress } from '../hooks/useCurrentAddress';
import type { InstituteFormValues } from '../schemas/institute.schema';
import { sharedMobilesWarning } from '../sharedMobiles';

/**
 * The four editable steps of the create-institute wizard.
 *
 * They share one react-hook-form instance rather than holding their own state,
 * so moving back and forth never loses a value and the whole form can be
 * submitted in a single request.
 */
export interface StepProps {
  register: UseFormRegister<InstituteFormValues>;
  errors: FieldErrors<InstituteFormValues>;
  control: Control<InstituteFormValues>;
  watch: UseFormWatch<InstituteFormValues>;
  /** Needed by the address step, where choosing a state resets the district. */
  setValue: UseFormSetValue<InstituteFormValues>;
  /** Set on the edit screen, where a few fields become read-only. */
  isEdit?: boolean;
  /** The last four of an Aadhaar already on file — the number itself never comes back. */
  savedAadhaarLast4?: string | null;
}

// --- step 1 -----------------------------------------------------------------

export function IdentityStep({
  register,
  errors,
  control,
  watch,
  isEdit = false,
  savedAadhaarLast4 = null,
}: StepProps) {
  const board = watch('board');
  /**
   * What kind of institute this is. Half of this wizard hangs off it — a college
   * has no board, no teaching band and no class range — so it is asked first,
   * before anything it decides.
   */
  const institutionType = watch('institutionType');
  const isCollege = institutionType === 'COLLEGE';

  return (
    <div className="flex flex-col gap-8">
      <FormSection
        title="Identity"
        description="How the institute is named in the product and addressed on its own URL."
      >
        <Select
          label="Kind of institute"
          options={INSTITUTION_TYPE_OPTIONS}
          // Immutable once created, like the code: the whole product branches on
          // it, and an institute with students already on classes cannot become a
          // college without those rows meaning something different.
          disabled={isEdit}
          className="py-2.5"
          hint={
            isEdit
              ? 'Cannot be changed once the institute exists.'
              : 'A college is asked for fewer details, and admits students on basic details.'
          }
          error={errors.institutionType?.message}
          {...register('institutionType')}
        />
        {/* Never typed: the server assigns the next code for this kind of
            institute (sch-0001, clg-0001) when it is saved, and it is immutable
            after — it is the reference receipts and our staff know the
            institute by. Shown here so the reader knows where it comes from. */}
        <Input
          label="Institute code"
          placeholder={isEdit ? undefined : 'Generated automatically when you save'}
          disabled
          hint={
            isEdit
              ? 'Assigned by the system and cannot be changed — it appears on receipts.'
              : 'Assigned by the system, so every institute has one in the same format.'
          }
          {...register('code')}
        />
        <Input
          label="Subdomain (optional)"
          placeholder="sunrise"
          hint="Becomes sunrise.yourproduct.in once DNS is set up."
          error={errors.subdomain?.message}
          {...register('subdomain')}
        />
        <FormFieldWide>
          <Input
            label="Registered name"
            placeholder="Sunrise Public Institute"
            hint="Exactly as printed on the registration certificate."
            error={errors.legalName?.message}
            required
            {...register('legalName')}
          />
        </FormFieldWide>
        <Input
          label="Display name"
          placeholder="Sunrise Public Institute"
          error={errors.displayName?.message}
          required
          {...register('displayName')}
        />
        <Input
          label="Short name (optional)"
          placeholder="SPS"
          hint="Used on receipts, report cards and SMS."
          error={errors.shortName?.message}
          {...register('shortName')}
        />
        <FormFieldWide>
          <Input
            label="Motto (optional)"
            placeholder="Knowledge is light"
            error={errors.motto?.message}
            {...register('motto')}
          />
        </FormFieldWide>
      </FormSection>

      <FormSection
        title="Recognition"
        description="Statutory details that appear on certificates and official reports."
      >
        <Controller
          name="udiseCode"
          control={control}
          render={({ field: { onChange, onBlur, value, ref } }) => {
            const rawDigits = (value || '').replace(/\D/g, '').slice(0, 11);
            const formatted =
              rawDigits.length > 8
                ? `${rawDigits.slice(0, 4)} ${rawDigits.slice(4, 8)} ${rawDigits.slice(8)}`
                : rawDigits.length > 4
                  ? `${rawDigits.slice(0, 4)} ${rawDigits.slice(4)}`
                  : rawDigits;

            return (
              <Input
                ref={ref}
                label="UDISE+ code"
                placeholder="2725 0612 345"
                // Digits only: a phone opens its number pad, and anything else
                // typed or pasted is dropped below. No maxLength — the browser
                // would cut a pasted "UDISE: 27250612345" before the digits
                // were picked out; the 11-digit cap below does that job.
                inputMode="numeric"
                autoComplete="off"
                value={formatted}
                onBlur={onBlur}
                hint={
                  rawDigits.length > 0
                    ? `${rawDigits.length} / 11 digits entered`
                    : '11-digit national institute code.'
                }
                error={errors.udiseCode?.message}
                onChange={(e) => {
                  const inputVal = e.target.value;
                  const digitsOnly = inputVal.replace(/\D/g, '').slice(0, 11);
                  onChange(digitsOnly);
                }}
              />
            );
          }}
        />
        {/* A college has no board affiliation, so it is not asked for one. */}
        {!isCollege && (
          <Select
            label="Board"
            className="py-2.5"
            options={BOARD_OPTIONS}
            error={errors.board?.message}
            {...register('board')}
          />
        )}
        {!isCollege && board === 'STATE' && (
          <Input
            label="State board name"
            placeholder="Maharashtra State Board"
            error={errors.stateBoardName?.message}
            {...register('stateBoardName')}
          />
        )}
        <Input
          label="Affiliation number (optional)"
          placeholder="1130456"
          error={errors.affiliationNumber?.message}
          {...register('affiliationNumber')}
        />
        <DatePicker
          label="Affiliation valid till (optional)"
          placeholder="Not recorded"
          hint="We warn the institute before it lapses."
          error={errors.affiliationValidTill?.message}
          {...register('affiliationValidTill')}
        />
        <Input
          label="Recognition number (optional)"
          error={errors.recognitionNumber?.message}
          {...register('recognitionNumber')}
        />
        <Input
          label="Established year (optional)"
          type="number"
          placeholder="1998"
          error={errors.establishedYear?.message}
          {...register('establishedYear')}
        />
        <Select
          label="Management type"
          options={MANAGEMENT_OPTIONS}
          error={errors.managementType?.message}
          {...register('managementType')}
        />
      </FormSection>

      <FormSection
        title="Legal entity"
        description="The trust or society that runs the institute — needed for invoices and certificates."
      >
        <Input
          label="Trust / society name (optional)"
          error={errors.trustName?.message}
          {...register('trustName')}
        />
        <Input
          label="Trust registration number (optional)"
          error={errors.trustRegistrationNo?.message}
          {...register('trustRegistrationNo')}
        />
        <Input
          label="PAN (optional)"
          placeholder="ABCDE1234F"
          hint="Encrypted at rest."
          error={errors.panNumber?.message}
          {...register('panNumber')}
        />
        <Input
          label="GSTIN (optional)"
          placeholder="27ABCDE1234F1Z5"
          hint="Only if the institute needs a GST invoice."
          error={errors.gstin?.message}
          {...register('gstin')}
        />
        <Input
          label="Aadhaar of authorised signatory (optional)"
          placeholder="2345 6789 0123"
          inputMode="numeric"
          autoComplete="off"
          hint={
            savedAadhaarLast4
              ? `One is on file, ending ${savedAadhaarLast4}. Leave blank to keep it, or type a new one to replace it.`
              : 'The trustee or correspondent who signs for the institute. Encrypted at rest.'
          }
          error={errors.aadhaarNumber?.message}
          {...register('aadhaarNumber')}
        />
      </FormSection>
    </div>
  );
}

interface DynamicListInputProps {
  label: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  value: string[] | undefined;
  onChange: (val: string[]) => void;
  addLabel: string;
  required?: boolean;
}

function DynamicListInput({
  label,
  placeholder,
  hint,
  error,
  value,
  onChange,
  addLabel,
  required = false,
}: DynamicListInputProps) {
  const list = Array.isArray(value) && value.length > 0 ? value : [''];

  function handleItemChange(index: number, newValue: string) {
    const next = [...list];
    next[index] = newValue;
    onChange(next);
  }

  function handleAddItem() {
    onChange([...list, '']);
  }

  function handleRemoveItem(index: number) {
    const next = list.filter((_, i) => i !== index);
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="lbl">
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {list.map((item, index) => (
          <div key={index} className="flex items-center gap-1">
            <div className="min-w-0 flex-1">
              <Input
                label={`${label} ${index + 1}`}
                hideLabel
                placeholder={placeholder}
                value={item}
                onChange={(e) => handleItemChange(index, e.target.value)}
              />
            </div>

            {list.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 text-ink-3 hover:bg-crit-bg hover:text-crit px-2.5 h-11"
                aria-label={`Remove ${label} ${index + 1}`}
                onClick={() => handleRemoveItem(index)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start text-xs gap-1.5"
          onClick={handleAddItem}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add {addLabel}
        </Button>

        {hint && <p className="text-xs text-ink-3">{hint}</p>}
      </div>

      {error && (
        <p role="alert" className="text-xs text-crit mt-0.5">
          {error}
        </p>
      )}
    </div>
  );
}

// --- step 2 -----------------------------------------------------------------

export function AcademicStep({ register, errors, control, watch, setValue, isEdit = false }: StepProps) {
  const isCollege = watch('institutionType') === 'COLLEGE';
  const streams = watch('streams');

  // Streams belong to classes XI–XII, which a college runs and a school here
  // does not, so a school is not asked for them. Switched from College to
  // School while adding, anything typed is cleared so it is not saved unseen.
  // Never on edit: the type is fixed there, and clearing would drop saved data.
  useEffect(() => {
    if (!isEdit && !isCollege && streams && streams.length > 0) {
      setValue('streams', [], { shouldDirty: true });
    }
  }, [isEdit, isCollege, streams, setValue]);

  return (
    <div className="flex flex-col gap-8">
      <FormSection
        title="What this institute teaches"
        description="Drives class setup, timetables and the report card format."
      >
        {/* "Upper Primary" means nothing to a college, so it gets its own list. */}
        {isCollege ? (
          <Select
            label="Institute level"
            options={COLLEGE_LEVEL_OPTIONS}
            error={errors.collegeLevel?.message}
            {...register('collegeLevel')}
          />
        ) : (
          <Select
            label="Institute level"
            options={SCHOOL_LEVEL_OPTIONS}
            error={errors.schoolLevel?.message}
            {...register('schoolLevel')}
          />
        )}
        <Select
          label="Lowest class"
          options={CLASS_LEVEL_OPTIONS}
          error={errors.lowestClass?.message}
          // The range message sits on the highest class, so re-check it when
          // the lowest one moves — or it stays stale until that field is touched.
          {...register('lowestClass', { deps: 'highestClass' })}
        />
        <Select
          label="Highest class"
          options={CLASS_LEVEL_OPTIONS}
          error={errors.highestClass?.message}
          {...register('highestClass')}
        />
        <Select
          label="Student intake"
          options={GENDER_TYPE_OPTIONS}
          error={errors.genderType?.message}
          {...register('genderType')}
        />
        <Select
          label="Day or residential"
          options={RESIDENTIAL_OPTIONS}
          error={errors.residentialType?.message}
          {...register('residentialType')}
        />
      </FormSection>

      <FormSection title={isCollege ? 'Languages and streams' : 'Languages'} columns={1}>
        <Controller
          name="mediumOfInstruction"
          control={control}
          render={({ field }) => (
            <DynamicListInput
              label="Medium of instruction"
              placeholder="e.g. English"
              addLabel="language"
              value={field.value}
              onChange={field.onChange}
              error={errors.mediumOfInstruction?.message}
              required
            />
          )}
        />
        {isCollege && (
          <Controller
            name="streams"
            control={control}
            render={({ field }) => (
              <DynamicListInput
                label="Senior secondary streams"
                placeholder="e.g. Science"
                addLabel="stream"
                hint="Leave empty if the institute does not run classes XI–XII."
                value={field.value}
                onChange={field.onChange}
                error={errors.streams?.message}
              />
            )}
          />
        )}
      </FormSection>
    </div>
  );
}

// --- step 3 -----------------------------------------------------------------

export function ContactStep({ register, errors, watch, setValue }: StepProps) {
  const state = watch('state');
  const district = watch('district');

  const districtOptions = useMemo(() => {
    const known = districtOptionsFor(state);
    // An institute saved before this list existed — or one in a district that has
    // since been renamed — must still show what it holds rather than appearing
    // blank, so an unrecognised value is offered alongside the official set.
    if (district && !known.some((option) => option.value === district)) {
      return [...known, { value: district, label: district }];
    }
    return known;
  }, [state, district]);

  const latitude = watch('latitude');
  const longitude = watch('longitude');
  const hasLocation = typeof latitude === 'number' && typeof longitude === 'number';

  /**
   * A district waiting for its state's list to be drawn. The district box is a
   * native select underneath, and a select cannot hold a value that is not one
   * of its options — set in the same tick as a new state, the district would
   * be dropped and the new list's first entry shown instead.
   */
  const [pendingDistrict, setPendingDistrict] = useState<string | null>(null);
  useEffect(() => {
    if (pendingDistrict === null) return;
    setValue('district', pendingDistrict, { shouldDirty: true, shouldValidate: true });
    setPendingDistrict(null);
  }, [pendingDistrict, districtOptions, setValue]);

  /**
   * "Use current location" replaces the whole address with the one at that
   * spot, because pressing it is the request to. Anything the map does not
   * know is *cleared* rather than left as it was — a Hyderabad street under a
   * Gujarat state is worse than a blank box that says what is missing. Every
   * box stays editable afterwards.
   */
  const current = useCurrentAddress(({ latitude: lat, longitude: lng, address }) => {
    const options = { shouldDirty: true, shouldValidate: true };
    setValue('latitude', lat, options);
    setValue('longitude', lng, options);
    if (!address) return [];

    const missing: string[] = [];

    // The server only ever answers with one of the accepted states, or null.
    const stateValue = INDIAN_STATES.find((name) => name === address.state);
    if (stateValue) setValue('state', stateValue, options);
    else missing.push('state');

    // Only a district the list actually offers, in the list's own spelling;
    // set once that list is on screen (see `pendingDistrict`).
    const districtValue = districtOptionsFor(stateValue ?? state).find(
      (option) => option.label.toLowerCase() === address.district?.toLowerCase(),
    )?.value;
    setPendingDistrict(districtValue ?? '');
    if (!districtValue) missing.push('district');

    setValue('addressLine1', address.addressLine1 ?? '', options);
    if (!address.addressLine1) missing.unshift('address line 1');
    setValue('locality', address.locality ?? '', options);
    setValue('city', address.city ?? '', options);
    if (!address.city) missing.push('city');
    setValue('pincode', address.pincode ?? '', options);
    if (!address.pincode) missing.push('PIN code');

    return missing;
  });

  // Clearing happens here rather than in an effect on purpose: an effect would
  // also fire when the edit screens call `reset()` with a loaded institute, and
  // would wipe a district that was already saved.
  const stateField = register('state');

  return (
    <div className="flex flex-col gap-8">
      <FormSection
        title="Campus address"
        description="Printed on certificates and fee receipts. Standing at the campus? Fill it from your location."
        aside={
          <Button
            variant="primary"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 border-blue-600 text-white shadow-xs font-bold text-sm"
            leftIcon={<LocateFixed className="h-3.5 w-3.5" />}
            isLoading={current.isLocating}
            loaderType="spinner"
            onClick={current.locate}
          >
            Use current location
          </Button>
        }
      >
        {current.status && (
          <FormFieldWide>
            <p
              role="status"
              className={
                current.status.tone === 'warning' ? 'text-xs font-[560] text-warn' : 'text-xs text-ink-2'
              }
            >
              {current.status.text}
            </p>
          </FormFieldWide>
        )}
        <FormFieldWide>
          <Input
            label="Address line 1"
            placeholder="Plot 24, Sector 8"
            error={errors.addressLine1?.message}
            required
            {...register('addressLine1')}
          />
        </FormFieldWide>
        <Input
          label="Address line 2 (optional)"
          error={errors.addressLine2?.message}
          {...register('addressLine2')}
        />
        <Input
          label="Locality (optional)"
          placeholder="Kothrud"
          error={errors.locality?.message}
          {...register('locality')}
        />
        {/* State first: it decides which districts the next field can offer. */}
        <Select
          label="State"
          placeholder="Select a state"
          options={STATE_OPTIONS}
          error={errors.state?.message}
          {...stateField}
          onChange={(event) => {
            stateField.onChange(event);
            // A district belongs to exactly one state, so the one already
            // chosen cannot survive a change of state.
            setValue('district', '', { shouldDirty: true });
          }}
        />
        <Select
          label="District"
          placeholder={state ? 'Select a district' : 'Choose a state first'}
          options={districtOptions}
          disabled={!state}
          error={errors.district?.message}
          required
          {...register('district')}
        />
        <Input label="City" placeholder="Pune" error={errors.city?.message} required {...register('city')} />
        <Input
          label="PIN code"
          placeholder="411038"
          inputMode="numeric"
          error={errors.pincode?.message}
          required
          {...register('pincode')}
        />

        {/* Latitude and longitude come only from the button — never typed —
            so they are shown, not offered as boxes. */}
        <FormFieldWide>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-field border border-line bg-surface-2 px-3 py-2 text-xs">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
            {hasLocation ? (
              <>
                <span className="text-ink-2">
                  Latitude <span className="font-[650] tabular-nums text-ink">{latitude}</span>
                </span>
                <span className="text-ink-2">
                  Longitude <span className="font-[650] tabular-nums text-ink">{longitude}</span>
                </span>
                <button
                  type="button"
                  className="ml-auto rounded-field font-semibold text-base text-red-600 dark:text-red-400 transition-colors hover:text-red-700 dark:hover:text-red-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                  onClick={() => {
                    const options = { shouldDirty: true, shouldValidate: true };
                    setValue('latitude', '', options);
                    setValue('longitude', '', options);
                    current.clearStatus();
                  }}
                >
                  Remove location
                </button>
              </>
            ) : (
              <span className="text-ink-3">
                No location yet — press “Use current location” at the campus to add it.
              </span>
            )}
          </div>
          {(errors.latitude?.message ?? errors.longitude?.message) && (
            <p className="mt-1 text-xs text-crit">{errors.latitude?.message ?? errors.longitude?.message}</p>
          )}
        </FormFieldWide>
      </FormSection>

      <FormSection
        title="Office contact"
        description="Phone numbers are encrypted at rest and shown masked to support staff."
      >
        <PhoneInput
          label="Office phone"
          error={errors.phone?.message}
          required
          {...register('phone')}
        />
        <PhoneInput
          label="Alternate phone (optional)"
          error={errors.alternatePhone?.message}
          {...register('alternatePhone')}
        />
        <PhoneInput
          label="WhatsApp number (optional)"
          hint="Used for parent notifications."
          error={errors.whatsappNumber?.message}
          {...register('whatsappNumber')}
        />
        <Input
          label="Official email"
          type="email"
          placeholder="office@institute.edu.in"
          hint="For contact only — it cannot sign in. Password emails go to the account administrator's email."
          error={errors.email?.message}
          required
          {...register('email')}
        />
        <Input
          label="Website (optional)"
          placeholder="https://www.institute.edu.in"
          error={errors.website?.message}
          {...register('website')}
        />
      </FormSection>
    </div>
  );
}

// --- step 4 -----------------------------------------------------------------

export function PeopleStep({
  register,
  errors,
  watch,
  showSubscription = true,
  warnSharedMobiles = false,
}: StepProps & {
  /**
   * The licence fee, payment and due date. Only the platform console edits
   * them; a school's own profile screen passes false.
   */
  showSubscription?: boolean;
  /** Notes when the office, principal and admin numbers repeat. Opted into by the create wizard. */
  warnSharedMobiles?: boolean;
}) {
  const sharedMobiles = warnSharedMobiles
    ? sharedMobilesWarning({
        phone: watch('phone'),
        principalPhone: watch('principalPhone'),
        adminContactPhone: watch('adminContactPhone'),
      })
    : null;

  return (
    <div className="flex flex-col gap-8">
      {sharedMobiles && (
        <Alert tone="warning" variant="note" title="Same mobile number">
          {sharedMobiles}
        </Alert>
      )}

      <FormSection title="Principal" description="Named on report cards and certificates.">
        <Input
          label="Principal name"
          placeholder="Dr. Anjali Deshpande"
          error={errors.principalName?.message}
          required
          {...register('principalName')}
        />
        <PhoneInput
          label="Principal phone"
          error={errors.principalPhone?.message}
          required
          {...register('principalPhone')}
        />
        <Input
          label="Principal email"
          type="email"
          error={errors.principalEmail?.message}
          required
          {...register('principalEmail')}
        />
      </FormSection>

      <FormSection
        title="Account administrator"
        description="This person receives the first login. We never set or see their password — they get a one-time link to choose it."
      >
        <Input
          label="Full name"
          error={errors.adminContactName?.message}
          required
          {...register('adminContactName')}
        />
        <Input
          label="Designation (optional)"
          placeholder="Head Clerk"
          error={errors.adminContactDesignation?.message}
          {...register('adminContactDesignation')}
        />
        <PhoneInput
          label="Mobile number"
          error={errors.adminContactPhone?.message}
          required
          {...register('adminContactPhone')}
        />
        <Input
          label="Email"
          type="email"
          hint="Their sign-in address. Set-password and forgot-password emails go only here, never to the official email."
          error={errors.adminContactEmail?.message}
          required
          {...register('adminContactEmail')}
        />
      </FormSection>

      {/* What they pay us, beside the person we bill. Not a step of its own:
          two fields do not earn a fifth screen in the wizard. */}
      {showSubscription && (
        <FormSection
          title="Subscription"
          description="What this institute pays for the software, when it is due, and whether it has been received."
        >
          <Input
            label="Licence fee (optional)"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            placeholder="125000"
            hint="In ₹. Leave blank until the fee is agreed."
            error={errors.subscriptionAmount?.message}
            {...register('subscriptionAmount')}
          />
          <Select
            label="Payment"
            className="py-2.5"
            options={PAYMENT_STATUS_OPTIONS}
            error={errors.paymentStatus?.message}
            {...register('paymentStatus')}
          />
          <DatePicker
            label="Due date (optional)"
            placeholder="Not agreed"
            hint="Shown on the customer card so overdue fees are easy to spot."
            error={errors.subscriptionDueDate?.message}
            {...register('subscriptionDueDate')}
          />
        </FormSection>
      )}
    </div>
  );
}
